import { z } from "zod";
import { ZDateish } from "./commonSchema";
import { ZObjectIdString } from "./issueBoardSchema";
const ZLooseDate = z.union([z.date(), z.string()]);
const ZNullableLooseDate = ZLooseDate.optional().nullable();
const ZUserPartial = z
    .object({
    _id: ZObjectIdString,
    name: z.string().default(""),
})
    .passthrough();
const ZUserOrId = z.union([ZObjectIdString, ZUserPartial]);
const ZNullableUserOrId = z.preprocess((value) => (value === "" ? null : value), ZUserOrId.optional().nullable());
const ZChecklistRef = z.union([ZObjectIdString, z.object({ _id: ZObjectIdString }).passthrough()]);
// --- DK (Disiplinkontroll) ---
export const ZChecklistDkApproval = z.object({
    userId: ZObjectIdString.optional(),
    name: z.string().optional().default("").nullable(),
    upn: z.string().optional().default("").nullable(),
    approvedAt: ZDateish.optional().nullable(),
});
// ----------------------------
export const ZChecklistConnectOrDisconnectIssuePOSTBody = z.object({
    checklistItemId: ZObjectIdString,
    issueId: ZObjectIdString,
    connectOrDisconnect: z.enum(["CONNECT", "DISCONNECT"]),
});
export const ZChecklistUpdateIdsParams = z.object({
    checklistId: ZObjectIdString,
});
export const ZRowMoveInfo = z.object({
    checklistId: ZObjectIdString,
    isCat: z.boolean(),
    oldCategoryId: ZObjectIdString,
    newCategoryId: ZObjectIdString.nullable(),
    rowId: ZObjectIdString,
    position: z.number(),
});
export const ZChecklistExcelImportRows = z
    .array(z.tuple([z.string(), z.string(), z.string().optional().nullable(), z.string().optional().nullable()]))
    .max(1000); // [["1", "row1"], ["1.1", "row2"], ["2", "row3"]...]
export const ZCheckType = z.object({
    active: z.boolean().optional(),
    name: z.string(),
});
export const ZPostBodyCheclistImportExcel = z.object({
    name: z.string(),
    pns: z.string().optional(),
    checkTypes: z.object({
        check1: ZCheckType,
        check2: ZCheckType,
        check3: ZCheckType,
    }),
    checklistImportRows: ZChecklistExcelImportRows,
    status: z.string(),
    templateType: z.enum(["MC", "PROJECT", ""]).optional(),
});
export const ZSingleCheckType = z.object({
    active: z.boolean(),
    name: z.string(),
});
export const ZCheckTypes = z.object({
    check1: ZSingleCheckType,
    check2: ZSingleCheckType,
    check3: ZSingleCheckType,
    check4: ZSingleCheckType.optional(),
    check5: ZSingleCheckType.optional(),
    check6: ZSingleCheckType.optional(),
    check7: ZSingleCheckType.optional(),
    check8: ZSingleCheckType.optional(),
    check9: ZSingleCheckType.optional(),
    check10: ZSingleCheckType.optional(),
    check11: ZSingleCheckType.optional(),
    check12: ZSingleCheckType.optional(),
    check13: ZSingleCheckType.optional(),
    check14: ZSingleCheckType.optional(),
    check15: ZSingleCheckType.optional(),
});
export const ZChecklistStatus = z.enum(["ACTIVE", "IN_DEVELOPMENT", "DELIVERED", "REMOVED", "INACTIVE"]);
export const ZChecklistTemplateTypesEnums = z.enum(["MC", "PROJECT", ""]);
export const ZAddChecklistBody = z.object({
    checklistTemplateId: z.union([ZObjectIdString, z.literal("0")]),
    name: z.string(),
    pns: z.string().nullable().optional(),
    checkTypes: ZCheckTypes,
    status: ZChecklistStatus,
    isTemplate: z.boolean(),
    templateType: ZChecklistTemplateTypesEnums,
    doksam: z
        .object({
        docId: z.string(),
        subTaskNo: z.string().optional().nullable(),
        preAssignUsersToChecks: z.object({
            check1UserUpn: z.string().optional(),
            check2UserUpn: z.string().optional(),
        }),
        // DK approval (overordnet godkjenning av hele sjekklista)
        dkApproval: ZChecklistDkApproval.optional(),
    })
        .optional(),
});
export const ZChecklistProgressSchema = z.object({
    accepted: z.number().default(0),
    rejected: z.number().default(0),
});
export const ZChecklistStampSettingsSchema = z
    .object({
    yes: z.object({ text: z.string(), color: z.string(), icon: z.string() }),
    no: z.object({ text: z.string(), color: z.string(), icon: z.string() }),
    na: z.object({ text: z.string(), color: z.string(), icon: z.string() }),
    ir: z.object({ text: z.string(), color: z.string(), icon: z.string() }),
    uav: z.object({ text: z.string(), color: z.string(), icon: z.string() }),
})
    .passthrough();
const ZChecklistDoksamSchema = z
    .object({
    docId: z.string().optional().nullable(),
    subTaskNo: z.string().optional().nullable(),
    reviewId: z.string().optional().nullable(),
    check1UserUpn: z.string().optional().nullable(),
    check2UserUpn: z.string().optional().nullable(),
    dkApproval: ZChecklistDkApproval.optional(),
})
    .passthrough();
const ZChecklistGeneratedFromTemplate = z
    .object({
    _id: ZObjectIdString,
    name: z.string(),
})
    .passthrough();
export const ZChecklistBaseSchema = z
    .object({
    _id: ZObjectIdString,
    createdAt: ZNullableLooseDate,
    updatedAt: ZNullableLooseDate,
    id: z.number().optional(),
    project: ZChecklistRef,
    name: z.string(),
    aboutText: z.string().optional().nullable(),
    templateDescription: z.string().default(""),
    templateType: ZChecklistTemplateTypesEnums,
    isTemplate: z.boolean(),
    status: ZChecklistStatus,
    tags: z.array(z.string()).default([]),
    checkTypes: ZCheckTypes,
    progress: ZChecklistProgressSchema.optional(),
    pns: z.string().optional().nullable(),
    dueDate: ZNullableLooseDate,
    stampSettings: ZChecklistStampSettingsSchema.optional(),
    templateCategory: z.string().optional().nullable(),
    includeIrOnDefaultRoles: z.boolean().default(false),
    includeUavOnDefaultRoles: z.boolean().default(false),
    phase: z.string().optional().nullable(),
    discipline: z.string().optional().nullable(),
    activity: z.string().optional().nullable(),
    itemTagsActive: z.boolean().optional(),
    itemTags: z.array(z.string()).optional(),
    activeItemTags: z.array(z.string()).optional(),
    revisionDate: z.string().optional().nullable(),
    revisionNr: z.string().optional().nullable(),
    activeItemTagChangedDate: z.string().optional().nullable(),
    doksam: ZChecklistDoksamSchema.optional(),
    documentCategoryCode: z.string().default("SKL"),
    documentCategoryCode2: z.string().default(""),
    disciplineCode: z.string().default(""),
    subDiscipline: z.string().default(""),
    competenceNetwork: z.string().default(""),
})
    .passthrough();
const ZChecklistDocumentManagersDbSchema = z
    .object({
    preparedBy: ZObjectIdString.optional().nullable(),
    checkedBy: ZObjectIdString.optional().nullable(),
    approvedBy: ZObjectIdString.optional().nullable(),
})
    .partial()
    .passthrough();
const ZChecklistDocumentManagersApiSchema = z
    .object({
    preparedBy: ZUserOrId.optional().nullable(),
    checkedBy: ZUserOrId.optional().nullable(),
    approvedBy: ZUserOrId.optional().nullable(),
})
    .partial()
    .passthrough();
export const ZChecklistDbSchema = ZChecklistBaseSchema.extend({
    lastUpdatedBy: ZObjectIdString.optional().nullable(),
    createdBy: ZObjectIdString.optional().nullable(),
    assignedTo: ZObjectIdString.optional().nullable(),
    admin: ZObjectIdString.optional().nullable(),
    activeItemTagChangedBy: ZObjectIdString.optional().nullable(),
    disciplineLeader: ZObjectIdString.optional().nullable(),
    documentManagers: ZChecklistDocumentManagersDbSchema.optional(),
    generatedFromTemplate: z.union([ZObjectIdString, ZChecklistGeneratedFromTemplate]).optional().nullable(),
});
const ZChecklistApiSchemaRaw = ZChecklistBaseSchema.extend({
    lastUpdatedBy: ZUserOrId.optional().nullable(),
    createdBy: ZUserOrId.optional().nullable(),
    assignedTo: ZUserOrId.optional().nullable(),
    admin: ZUserOrId.optional().nullable(),
    activeItemTagChangedBy: ZUserOrId.optional().nullable(),
    disciplineLeader: ZUserOrId.optional().nullable(),
    documentManagers: ZChecklistDocumentManagersApiSchema.optional(),
    generatedFromTemplate: z.union([ZObjectIdString, ZChecklistGeneratedFromTemplate]).optional().nullable(),
});
export const ZChecklistApiSchema = ZChecklistApiSchemaRaw;
const ZChecklistCategoryCheckSchema = z
    .object({
    assignedTo: ZNullableUserOrId,
})
    .passthrough();
const ZChecklistCategoryChecksSchema = z.object({
    check1: ZChecklistCategoryCheckSchema.optional(),
    check2: ZChecklistCategoryCheckSchema.optional(),
    check3: ZChecklistCategoryCheckSchema.optional(),
    check4: ZChecklistCategoryCheckSchema.optional(),
    check5: ZChecklistCategoryCheckSchema.optional(),
    check6: ZChecklistCategoryCheckSchema.optional(),
    check7: ZChecklistCategoryCheckSchema.optional(),
    check8: ZChecklistCategoryCheckSchema.optional(),
    check9: ZChecklistCategoryCheckSchema.optional(),
    check10: ZChecklistCategoryCheckSchema.optional(),
    check11: ZChecklistCategoryCheckSchema.optional(),
    check12: ZChecklistCategoryCheckSchema.optional(),
    check13: ZChecklistCategoryCheckSchema.optional(),
    check14: ZChecklistCategoryCheckSchema.optional(),
    check15: ZChecklistCategoryCheckSchema.optional(),
});
export const ZChecklistCategoryBaseSchema = z
    .object({
    _id: ZObjectIdString,
    project: ZChecklistRef.optional(),
    title: z.string(),
    collapsed: z.boolean().optional(),
    dueDate: z.string().optional().nullable(),
    checklist: ZChecklistRef,
    categoryId: z.number(),
    checks: ZChecklistCategoryChecksSchema.optional(),
})
    .passthrough();
export const ZChecklistCategoryDbSchema = ZChecklistCategoryBaseSchema;
const ZChecklistCategoryApiSchemaRaw = ZChecklistCategoryBaseSchema;
export const ZChecklistCategoryApiSchema = ZChecklistCategoryApiSchemaRaw;
export const ZChecklistItemStatus = z.enum(["NO", "N/A", "YES", "IR", "DISABLED", "UAV"]);
const ZChecklistItemCheckCommentSchema = z
    .object({
    createdBy: ZNullableUserOrId,
    text: z.string().default(""),
    createdDate: ZNullableLooseDate,
})
    .passthrough();
export const ZChecklistItemCheckSchema = z
    .object({
    status: ZChecklistItemStatus,
    updatedDate: ZNullableLooseDate,
    updatedBy: ZNullableUserOrId,
    comment: ZChecklistItemCheckCommentSchema.optional().nullable(),
})
    .passthrough();
export const ZChecklistItemChecksSchema = z.object({
    check1: ZChecklistItemCheckSchema,
    check2: ZChecklistItemCheckSchema,
    check3: ZChecklistItemCheckSchema,
    check4: ZChecklistItemCheckSchema,
    check5: ZChecklistItemCheckSchema,
    check6: ZChecklistItemCheckSchema,
    check7: ZChecklistItemCheckSchema,
    check8: ZChecklistItemCheckSchema,
    check9: ZChecklistItemCheckSchema,
    check10: ZChecklistItemCheckSchema,
    check11: ZChecklistItemCheckSchema,
    check12: ZChecklistItemCheckSchema,
    check13: ZChecklistItemCheckSchema,
    check14: ZChecklistItemCheckSchema,
    check15: ZChecklistItemCheckSchema,
});
export const ZChecklistInactiveSchema = z
    .object({
    isChecked: z.boolean(),
    updatedDate: ZNullableLooseDate,
    updatedBy: ZNullableUserOrId,
})
    .passthrough();
const ZChecklistItemBaseSchemaRaw = z
    .object({
    _id: ZObjectIdString,
    project: ZChecklistRef.optional(),
    checklist: ZChecklistRef,
    inactive: ZChecklistInactiveSchema,
    title: z.string(),
    reference: z.string().optional().nullable(),
    comment: z.string().optional().nullable(),
    category: ZChecklistRef,
    itemId: z.number(),
    checks: ZChecklistItemChecksSchema,
    comments: z.array(z.any()).optional(),
    itemTags: z.array(z.string()).optional(),
})
    .passthrough();
export const ZChecklistItemBaseSchema = ZChecklistItemBaseSchemaRaw;
export const ZChecklistItemDbSchema = ZChecklistItemBaseSchemaRaw.extend({
    connectedIssues: z.array(ZObjectIdString).optional(),
});
const ZChecklistItemApiSchemaRaw = ZChecklistItemBaseSchemaRaw.extend({
    connectedIssues: z.array(z.any()).optional(),
});
export const ZChecklistItemApiSchema = ZChecklistItemApiSchemaRaw;
const ZUpdatedChecklistItemApiSchemaRaw = ZChecklistItemBaseSchemaRaw.extend({
    connectedIssues: z.array(z.any()).optional(),
    progress: ZChecklistProgressSchema,
});
export const ZUpdatedChecklistItemApiSchema = ZUpdatedChecklistItemApiSchemaRaw;
const ZChecklistPartsApiSchemaRaw = z.object({
    checklist: ZChecklistApiSchemaRaw.nullable(),
    categories: z.array(ZChecklistCategoryApiSchemaRaw),
    items: z.array(ZChecklistItemApiSchemaRaw),
});
export const ZChecklistPartsApiSchema = ZChecklistPartsApiSchemaRaw;
const ZChecklistTagSchemaRaw = z
    .object({
    _id: z.string(),
})
    .passthrough();
export const ZChecklistTagSchema = ZChecklistTagSchemaRaw;
const ZChecklistRowPositionOutputSchemaRaw = z.union([
    z.array(ZChecklistCategoryApiSchemaRaw),
    z.array(ZChecklistItemApiSchemaRaw),
]);
export const ZChecklistRowPositionOutputSchema = ZChecklistRowPositionOutputSchemaRaw;
// Compact checklist projection used by template selector routes.
export const ZChecklistTemplateListItemSchema = z.object({
    _id: ZObjectIdString,
    id: z.number(),
    status: ZChecklistStatus,
    name: z.string(),
    disciplineCode: z.string(),
    templateDescription: z.string(),
    documentCategoryCode: z.string(),
    documentCategoryCode2: z.string(),
    subDiscipline: z.string(),
    competenceNetwork: z.string(),
});
export const ZChecklistReportData = z.object({
    id: z.number(),
    checklistTitle: z.string(),
    shortDate: z.string(),
    projectNo: z.number(),
    projectName: z.string(),
    cOneNames: z.string(),
    cOneShortNames: z.string(),
    cTwoNames: z.string(),
    cTwoShortNames: z.string(),
    cThreeNames: z.string(),
    cThreeShortNames: z.string(),
    check1Date: z.string(),
    check2Date: z.string(),
    check3Date: z.string(),
    checks: z.string(),
    cOne: z.string(),
    cTwo: z.string(),
    cThree: z.string(),
    categories: z.array(z.any()),
    hasAboutText: z.boolean(),
    aboutText: z.string(),
    phase: z.string(),
    discipline: z.string(),
    activity: z.string(),
    preparedBy: z.string(),
    checkedBy: z.string(),
    approvedBy: z.string(),
    hasItemTags: z.boolean(),
    activeItemTags: z.string().optional(),
    activeItemTagChangedBy: z.string().optional(),
    activeItemTagDate: z.string().optional(),
    revNr: z.string(),
    revDate: z.string(),
    signedBy: z.string(),
    cDkCoreName: z.string(),
    cDkCoreShortName: z.string(),
});
export var ChecklistTemplateName;
(function (ChecklistTemplateName) {
    ChecklistTemplateName["CHECKLIST_1COL"] = "checklist_1col.docx";
    ChecklistTemplateName["CHECKLIST_2COL"] = "checklist_2col.docx";
    ChecklistTemplateName["CHECKLIST_3COL"] = "checklist_3col.docx";
})(ChecklistTemplateName || (ChecklistTemplateName = {}));
